const steps = [
  {
    number: '01',
    icon: 'ri-wallet-3-line',
    title: 'Connect Wallet',
    tag: 'Secure Handshake',
    time: 'Under 30 sec',
    desc: 'Initiate a secure, read-only wallet connection via MetaMask or Trust Wallet. BPI\'s system performs an encrypted TLS 1.3 handshake to authenticate the session. No private keys are ever transmitted or stored — the process is fully non-custodial and zero-risk.',
    bullets: ['MetaMask & Trust Wallet', 'Read-only connection', 'TLS 1.3 encrypted', 'Zero-custody'],
    dark: false,
  },
  {
    number: '02',
    icon: 'ri-scan-2-line',
    title: 'Automated Smart Contract Audit',
    tag: 'Deep Scan',
    time: '~2 minutes',
    desc: "Our engine decompiles the full ERC-20 bytecode of the submitted contract. It maps every function signature, identifies hidden administrative controls (freeze/burn/transfer restrictions), detects honey pot logic, and flags any deviation from the official Tether or Circle canonical contracts.",
    bullets: ['Full bytecode decompile', 'Hidden freeze/burn detect', 'Honey pot identification', 'Canonical comparison'],
    dark: true,
  },
  {
    number: '03',
    icon: 'ri-list-check-3',
    title: 'Global Blacklist Cross-Reference',
    tag: 'Multi-Layer Screening',
    time: '~2 minutes',
    desc: 'The wallet address and contract are cross-referenced against 50+ global compliance databases simultaneously — OFAC SDN, Swiss FINMA, Tether & Circle official blacklists, Interpol financial crime feeds, EU consolidated sanctions, and proprietary on-chain forensics databases, all updated in real time.',
    bullets: ['OFAC SDN & FINMA', 'Tether / Circle registries', 'EU & UN sanctions', '50+ live feeds'],
    dark: false,
  },
  {
    number: '04',
    icon: 'ri-file-shield-2-line',
    title: 'Digital Certificate Issuance',
    tag: 'Instant PDF / On-Chain',
    time: 'Under 30 sec',
    desc: 'Upon successful completion of all prior phases, BPI Holding SA issues a digitally-signed BPI Compliance Certificate — delivered as an institutional-grade PDF with optional on-chain metadata attestation. Confirms the asset is clean, the contract is active, and the wallet is not blacklisted.',
    bullets: ['Digitally signed by BPI SA', 'Instant PDF delivery', 'On-chain attestation option', '90-day validity'],
    dark: true,
  },
];

export default function CertificationProcess() {
  return (
    <section id="certification" className="bg-white py-16 sm:py-24 px-5 sm:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="mb-12 sm:mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-emerald-500 rounded-full"></div>
            <span
              className="text-slate-500 text-xs font-bold tracking-[0.2em] uppercase"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              The 5-Minute Verification Process
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 leading-[1.08] tracking-tight max-w-xl"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Four Steps to Your
              <br />
              <span className="text-emerald-600">BPI Certificate.</span>
            </h2>
            <p className="text-slate-500 text-base leading-relaxed max-w-md" style={{ fontFamily: 'Inter, sans-serif' }}>
              Fully automated, institutionally rigorous, and recognized under Swiss law. From
              wallet connection to certified compliance in under 5 minutes.
            </p>
          </div>
        </div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 mb-10 sm:mb-14">
          {steps.map((step) => (
            <div
              key={step.number}
              className={`relative overflow-hidden rounded-xl sm:rounded-2xl p-7 sm:p-10 ${
                step.dark ? 'bg-[#001529]' : 'bg-slate-50 border border-slate-200'
              }`}
            >
              {/* Watermark number */}
              <div
                className={`absolute -bottom-3 -right-1 text-8xl font-black select-none pointer-events-none leading-none ${
                  step.dark ? 'text-white/[0.04]' : 'text-slate-900/[0.04]'
                }`}
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {step.number}
              </div>

              <div className="relative z-10">
                {/* Top metadata */}
                <div className="flex items-center justify-between gap-3 mb-5">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        step.dark ? 'bg-emerald-500/15' : 'bg-emerald-50'
                      }`}
                    >
                      <i className={`${step.icon} text-xl ${step.dark ? 'text-emerald-400' : 'text-emerald-600'}`}></i>
                    </div>
                    <span
                      className={`text-xs font-bold tracking-widest uppercase ${
                        step.dark ? 'text-slate-500' : 'text-slate-400'
                      }`}
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Step {step.number}
                    </span>
                  </div>
                  <div
                    className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                      step.dark
                        ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                        : 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                    }`}
                  >
                    <i className="ri-timer-line text-xs"></i>
                    {step.time}
                  </div>
                </div>

                {/* Title */}
                <h3
                  className={`text-xl sm:text-2xl font-black mb-1 leading-snug ${
                    step.dark ? 'text-white' : 'text-slate-900'
                  }`}
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {step.title}
                </h3>
                <div
                  className={`text-xs font-bold uppercase tracking-[0.15em] mb-4 ${
                    step.dark ? 'text-emerald-400' : 'text-emerald-600'
                  }`}
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {step.tag}
                </div>

                {/* Description */}
                <p
                  className={`text-sm sm:text-base leading-relaxed mb-5 ${
                    step.dark ? 'text-slate-400' : 'text-slate-500'
                  }`}
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {step.desc}
                </p>

                {/* Bullets */}
                <div className="grid grid-cols-2 gap-y-1.5 gap-x-4">
                  {step.bullets.map((b, j) => (
                    <div key={j} className="flex items-center gap-2">
                      <i
                        className={`ri-check-line text-xs flex-shrink-0 ${
                          step.dark ? 'text-emerald-400' : 'text-emerald-600'
                        }`}
                      ></i>
                      <span
                        className={`text-xs font-medium ${
                          step.dark ? 'text-slate-300' : 'text-slate-600'
                        }`}
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {b}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Flow line — desktop */}
        <div className="hidden sm:flex items-center justify-center gap-2 mb-12">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex items-center gap-2 border border-slate-200 bg-slate-50 rounded-full px-4 py-1.5">
                <i className={`${s.icon} text-xs text-slate-500`}></i>
                <span className="text-slate-500 text-xs font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {s.title}
                </span>
              </div>
              {i < steps.length - 1 && (
                <i className="ri-arrow-right-line text-slate-300 text-base"></i>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="bg-[#001529] rounded-2xl p-7 sm:p-12 flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-10">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 bg-emerald-500/15 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-award-fill text-3xl text-emerald-400"></i>
            </div>
            <div>
              <div
                className="text-white font-black text-lg sm:text-xl leading-tight mb-1"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Receive Your BPI Verification Certificate
              </div>
              <div className="text-slate-400 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                Issued by BPI Holding SA, Geneva. Digitally signed. FATF &amp; FINMA aligned. Delivered as instant PDF.
              </div>
            </div>
          </div>
          <a
            href="https://mycryptoaml.com/verification"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-2.5 bg-emerald-500 hover:bg-emerald-400 text-white px-8 py-4 rounded-lg font-black text-sm sm:text-base transition-all duration-200 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            <i className="ri-shield-check-line text-lg"></i>
            Verify Token &amp; Get Certificate
          </a>
        </div>
      </div>
    </section>
  );
}
